import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class TimingDevice extends BaseObj {
    public ID: number;
    public __id: number;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: { ID?: number; EntityState?: EntityState }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<TimingDevice>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('TimingDevice tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of TimingDevice from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: { ID?: number; EntityState?: 'Added' | 'Unchanged' }): TimingDevice {
        return new TimingDevice(values);
    }

    public static _FromServerJson(json: any): TimingDevice {
        return new TimingDevice({ ID: json.ID, EntityState: 'Unchanged' });
    }

    /**
     * Creates an instance of TimingDevice from another instance of TimingDevice.
     * @param other The TimingDevice you want to clone
     * @returns A cloned TimingDevice
     */
    public static FromOther(other: TimingDevice): TimingDevice {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return TimingDevice._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return TimingDevice.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: 'ID'[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'TimingDevice';
}
